'use client';

import { type UnitAdd } from '@/domain/products/types';
import { useBasicUnits } from '@/domain/units/hooks';
import { type BasicUnit } from '@/domain/units/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { FormControlLabel } from '@andritzot/metris-web-ui/inputs/form-control';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Switch } from '@andritzot/metris-web-ui/inputs/switch';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { DataGrid, type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import { Add } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCallback, useMemo } from 'react';

interface TargetBusinessAreasSectionProps {
  viewOnly: boolean;
}

type UnitRow = UnitAdd & { rowKey: number };

export function TargetBusinessAreasSection({ viewOnly }: Readonly<TargetBusinessAreasSectionProps>) {
  const unitsImpact = useRoadmapEditStore((s) => s.editableRoadMap.unitsImpact);
  const { data: basicUnits = [] } = useBasicUnits();

  const rows: UnitRow[] = useMemo(() => unitsImpact.map((u, i) => ({ ...u, rowKey: i })), [unitsImpact]);

  const updateStore = (updated: UnitAdd[]) =>
    useRoadmapEditStore.setState((state) => ({
      editableRoadMap: { ...state.editableRoadMap, unitsImpact: updated },
      isStoreDirty: true,
    }));

  const handleUpdate = useCallback(
    (index: number, patch: Partial<UnitAdd>) => {
      const isTogglingOn = patch.isRequested === true;
      updateStore(
        unitsImpact.map((u, i) => {
          if (i === index) return { ...u, ...patch };
          return isTogglingOn ? { ...u, isRequested: false } : u;
        }),
      );
    },
    [unitsImpact],
  );

  const handleDelete = useCallback(
    (index: number) => {
      updateStore(unitsImpact.filter((_, i) => i !== index));
    },
    [unitsImpact],
  );

  const handleAdd = () => {
    updateStore([...unitsImpact, { unitId: 0, isRequested: false }]);
  };

  const columns = useMemo<GridColDef<UnitRow>[]>(() => {
    const unitCol: GridColDef<UnitRow> = {
      field: 'unitId',
      headerName: 'Unit',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const selectedUnit = basicUnits.find((u) => u.unitId === row.unitId) ?? null;
        return (
          <Autocomplete<BasicUnit, false, false, false>
            options={basicUnits}
            getOptionLabel={(u) => u.unitName}
            isOptionEqualToValue={(a, b) => a.unitId === b.unitId}
            value={selectedUnit}
            onChange={(_, val) => handleUpdate(row.rowKey, { unitId: val?.unitId ?? 0 })}
            disabled={viewOnly}
            size="small"
            fullWidth
          />
        );
      },
    };

    const requestedCol: GridColDef<UnitRow> = {
      field: 'isRequested',
      headerName: 'Requested By',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <FormControlLabel
          control={
            <Switch
              checked={row.isRequested ?? false}
              onChange={(e) => handleUpdate(row.rowKey, { isRequested: e.target.checked })}
              size="small"
              disabled={viewOnly}
            />
          }
          label="Requested By"
          sx={{ m: 0 }}
        />
      ),
    };

    const actionsCol: GridColDef<UnitRow> = {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <IconButton variant="outlined" size="small" onClick={() => handleDelete(row.rowKey)} aria-label="Remove unit">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    };

    return viewOnly ? [unitCol, requestedCol] : [unitCol, requestedCol, actionsCol];
  }, [basicUnits, viewOnly, handleUpdate, handleDelete]);

  return (
    <Box>
      <Typography variant="subtitle2">Target Business Areas</Typography>
      {!viewOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton variant="outlined" color="primary" size="small" onClick={handleAdd}>
            <Add fontSize="small" />
          </IconButton>
        </Box>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.rowKey}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        aria-label="Target Business Areas"
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': { py: 0.5, alignItems: 'center', display: 'flex' },
        }}
      />
    </Box>
  );
}
